import { useDebounce } from '@uidotdev/usehooks'
import { useState } from 'react'

export type SortOrder = 'asc' | 'desc'

export interface PaginationParams {
  paginate: 1 // Whether to paginate results (1 for true, 0 for false)
  per_page: number // Number of items per page
  page: number // Current page number
  search?: string // Optional search term
  sort_by?: string // Field to sort by
  sort_order?: SortOrder // Sort order (asc or desc)
}

function usePagination(initialSortBy: string = '') {
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState<string>(initialSortBy)
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')
  const debouncedValue = useDebounce(search, 500)

  function onPerPageChange(newPerPage: number) {
    setCurrentPage(1)
    setPerPage(newPerPage)
  }

  function onSearchChange(value: string) {
    setSearch(value)
    setCurrentPage(1)
  }

  function onSortChange(field: string) {
    // If clicking on the same field, toggle the sort order
    if (field === sortBy) {
      setSortOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'))
    } else {
      // If clicking on a new field, set it as the sort field and default to ascending
      setSortBy(field)
      setSortOrder('asc')
    }
    // Reset to first page when changing sort
    setCurrentPage(1)
  }

  return {
    currentPage,
    setCurrentPage,
    perPage,
    onSearchChange,
    search,
    debouncedValue,
    setPerPage: onPerPageChange,
    // New sorting properties and methods
    sortBy,
    sortOrder,
    setSortBy,
    setSortOrder,
    onSortChange,
  }
}

export { usePagination }